// apps/web/src/features/cargo/useCargoWorkflows.ts
//
// Orchestrates the Cargo registry workflows (#1232): which modal is open, the
// per-row ability predicates, and the inline mutation handlers (client-tariff)
// that the action cells call. Keeping this in a hook keeps
// `CargoRegistryPage` declarative and under the size limit.
//
// The inline tariff mutation takes the row id as a MUTATION VARIABLE (not a
// hook-bound id), so a single mutation can target whichever row the actor
// clicked; on success both the list and summary invalidate.

import { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { CargoArticleWire } from '@magimp/shared';

import { useAuthStore } from '../../stores/auth.js';
import { useToastStore } from '../../stores/toast.js';
import { errorMessage } from '../../api/errors.js';
import { setCargoClientTariff } from '../../api/cargo-write.js';
import {
  canCreateCargoRow,
  canDeleteCargoRow,
  canEditCargoRow,
  canEditClientTariff,
  canImportCargoFiles,
  canReadCargoRow,
  type CargoActor,
} from './ability.js';
import { cargoKeys } from './queries.js';
import type { CargoRowActions } from './components/cargoActionCells.js';

export type CargoModalKind = 'add' | 'edit' | 'view' | 'payment' | 'delete' | 'import';

export interface CargoModalState {
  kind: CargoModalKind;
  row?: CargoArticleWire;
}

export interface CargoWorkflows {
  modal: CargoModalState | null;
  openAdd: () => void;
  openImport: () => void;
  openEdit: (row: CargoArticleWire) => void;
  closeModal: () => void;
  /** Row-click handler: edit for editors, otherwise a read-only card for readers. */
  onRowClick: (row: CargoArticleWire) => void;
  /** Action callbacks + per-row ability for the interactive cells. */
  rowActions: CargoRowActions;
  canEditRows: boolean;
  canOpenRows: boolean;
  canAdd: boolean;
  canImport: boolean;
  /** Forwarded from the list response envelope; page + modal consume it. */
  readOnlyMirror: boolean;
}

export function useCargoWorkflows(readOnlyMirror: boolean): CargoWorkflows {
  const { t } = useTranslation(['cargo']);
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const permissions = useAuthStore((s) => s.permissions);
  const actor = useMemo<CargoActor>(() => ({ user, permissions }), [user, permissions]);
  const showToast = useToastStore((s) => s.show);

  const [modal, setModal] = useState<CargoModalState | null>(null);

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: cargoKeys.all });
  };

  const tariff = useMutation<void, Error, { id: string; value: string | null }>({
    mutationFn: ({ id, value }) => setCargoClientTariff(id, value),
    onSuccess: invalidate,
  });

  const canEditRows = canEditCargoRow(actor) && !readOnlyMirror;
  const canOpenRows = canReadCargoRow(actor);
  const canAdd = canCreateCargoRow(actor) && !readOnlyMirror;
  const canImport = canImportCargoFiles(actor) && !readOnlyMirror;

  const rowActions = useMemo<CargoRowActions>(
    () => ({
      canDelete: canDeleteCargoRow(actor) && !readOnlyMirror,
      canEditTariff: (row) => canEditClientTariff(actor, row) && !readOnlyMirror,
      onSaveClientTariff: (row, value) =>
        tariff.mutate(
          { id: row.id, value },
          {
            onSuccess: () => showToast({ tone: 'success', text: t('cargo:tariff.saved') }),
            onError: (err) => showToast({ tone: 'error', text: errorMessage(err) }),
          },
        ),
      onOpenPaymentModal: (row) => setModal({ kind: 'payment', row }),
      onRequestDelete: (row) => setModal({ kind: 'delete', row }),
    }),
    [actor, readOnlyMirror, tariff, showToast, t],
  );

  return {
    modal,
    openAdd: () => {
      if (canAdd) setModal({ kind: 'add' });
    },
    openImport: () => {
      if (canImport) setModal({ kind: 'import' });
    },
    openEdit: (row) => setModal({ kind: 'edit', row }),
    closeModal: () => setModal(null),
    onRowClick: (row) => {
      if (canEditRows) setModal({ kind: 'edit', row });
      else if (canOpenRows) setModal({ kind: 'view', row });
    },
    rowActions,
    canEditRows,
    canOpenRows,
    canAdd,
    canImport,
    readOnlyMirror,
  };
}
